import { Plugin } from '@ckeditor/ckeditor5-core';
import RecorderStyleCommand from './recorderstylecommand';
import RecorderUtils from '../recorderutils';
import utils from './utils';
import { viewToModelStyleAttribute, modelToViewStyleAttribute } from './converters';

export default class RecorderStyleEditing extends Plugin {
    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'RecorderStyleEditing';
    }

    /**
     * @inheritDoc
     */
    static get requires() {
        return [ RecorderUtils ];
    }

    /**
     * @inheritDoc
     */
    init() {
        const { normalizeStyles, getDefaultStylesConfiguration } = utils;
        const editor = this.editor;

        const isBlockPluginLoaded = editor.plugins.has('RecorderBlockEditing');
        const isInlinePluginLoaded = editor.plugins.has('RecorderInlineEditing');

        //  Configuración por defecto de estilos
        editor.config.define(
            'recorder.styles',
            getDefaultStylesConfiguration(isBlockPluginLoaded, isInlinePluginLoaded)
        );

        this.normalizedStyles = normalizeStyles({
            configuredStyles: editor.config.get('recorder.styles'),
            isBlockPluginLoaded,
            isInlinePluginLoaded
        });

        // Conversions (modelo <-> vista)
        this._setupConversion(isBlockPluginLoaded, isInlinePluginLoaded);

        // Postfixer para limpiar atributos inválidos
        this._setupPostFixer();

        // Registrar comando principal
        editor.commands.add(
            'recorderStyle',
            new RecorderStyleCommand(editor, this.normalizedStyles)
        );
    }

    _setupConversion(isBlockPluginLoaded, isInlinePluginLoaded) {
        const editor = this.editor;
        const schema = editor.model.schema;
        const modelToViewConverter = modelToViewStyleAttribute(this.normalizedStyles);
        const viewToModelConverter = viewToModelStyleAttribute(this.normalizedStyles);

        editor.editing.downcastDispatcher.on('attribute:recorderStyle', modelToViewConverter);
        editor.data.downcastDispatcher.on('attribute:recorderStyle', modelToViewConverter);

        if (isBlockPluginLoaded) {
            schema.extend('recorderBlock', { allowAttributes: 'recorderStyle' });
            editor.data.upcastDispatcher.on('element:figure', viewToModelConverter, { priority: 'low' });
        }

        if (isInlinePluginLoaded) {
            schema.extend('recorderInline', { allowAttributes: 'recorderStyle' });
            editor.data.upcastDispatcher.on('element:audio', viewToModelConverter, { priority: 'low' });
            editor.data.upcastDispatcher.on('element:video', viewToModelConverter, { priority: 'low' });
        }
    }

    _setupPostFixer() {
        const editor = this.editor;
        const document = editor.model.document;
        const recorderUtils = editor.plugins.get(RecorderUtils);

        const stylesMap = new Map(
            this.normalizedStyles.map(style => [style.name, style])
        );

        document.registerPostFixer(writer => {
            let changed = false;

            for (const change of document.differ.getChanges()) {
                if (
                    change.type === 'insert' ||
                    (change.type === 'attribute' && change.attributeKey === 'recorderStyle')
                ) {
                    let element =
                        change.type === 'insert'
                            ? change.position.nodeAfter
                            : change.range.start.nodeAfter;

                    if (element && element.is('element', 'paragraph') && element.childCount > 0) {
                        element = element.getChild(0);
                    }

                    if (!recorderUtils.isRecorder(element)) {
                        continue;
                    }

                    const recorderStyle = element?.getAttribute('recorderStyle');
                    if (!recorderStyle) {
                        continue;
                    }

                    const recorderStyleDefinition = stylesMap.get(recorderStyle);
                    if (
                        !recorderStyleDefinition ||
                        !recorderStyleDefinition.modelElements.includes(element.name)
                    ) {
                        writer.removeAttribute('recorderStyle', element);
                        changed = true;
                    }
                }
            }
            return changed;
        });
    }
}
